import { useState, useEffect } from 'react';
import { Textarea, Button, Box, VStack, Text, Alert } from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster';
import api from '@/utils/api';

interface Props {
  workspaceId: number;
  fieldId: number;
  onSuccess?: () => void;
}

interface FieldOption {
  id: number;
  option_value: string;
  option_label: string;
  is_default: boolean;
}

export default function AddOptions({ workspaceId, fieldId, onSuccess }: Props) {
  const [options, setOptions] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [existingOptions, setExistingOptions] = useState<FieldOption[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Fetch existing options when component mounts or fieldId changes
  useEffect(() => {
    const fetchExistingOptions = async () => {
      if (!fieldId) return;

      setIsLoading(true);
      try {
        const response = await api.get(`/fields/${fieldId}/options`);
        setExistingOptions(response.data.data || []);
      } catch (error) {
        console.error('Failed to load existing options:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchExistingOptions();
  }, [fieldId]);

  // Parse and validate options input
  const validateOptions = (optionsInput: string) => {
    const errors: string[] = [];
    const lines = optionsInput.split('\n').filter((line) => line.trim());
    const newOptionValues = new Set<string>();

    lines.forEach((line, index) => {
      const parts = line.split(',');

      if (parts.length !== 2) {
        errors.push(
          `Line ${index + 1}: Each line must contain a label and value separated by a comma`,
        );
        return;
      }

      const [label, value] = [parts[0].trim(), parts[1].replace(/\s+/g, '')];

      if (!label) {
        errors.push(`Line ${index + 1}: Option label cannot be empty`);
      }

      if (!value) {
        errors.push(`Line ${index + 1}: Option value cannot be empty`);
        return;
      }

      // Check if value already exists in the database
      if (existingOptions.some((option) => option.option_value === value)) {
        errors.push(`Line ${index + 1}: Value "${value}" already exists in the database`);
        return;
      }

      // Check if value is duplicated in the current input
      if (newOptionValues.has(value)) {
        errors.push(`Line ${index + 1}: Duplicate value "${value}" in input`);
        return;
      }

      newOptionValues.add(value);
    });

    return errors;
  };

  async function onSubmit() {
    // First validate all options
    const errors = validateOptions(options);
    setValidationErrors(errors);

    if (errors.length > 0) {
      return; // Don't submit if there are validation errors
    }

    try {
      setIsSubmitting(true);
      const lines = options.split('\n').filter((line) => line.trim());
      let successCount = 0;

      // Use Promise.all to wait for all requests to complete
      await Promise.all(
        lines.map(async (line) => {
          const [optionLabel, rawValue] = line.split(',');
          const trimmedLabel = optionLabel.trim();
          const optionValue = rawValue.replace(/\s+/g, '');

          try {
            await api.post(`field-options`, {
              is_default: false,
              option_label: trimmedLabel,
              option_value: optionValue,
              workspace_id: workspaceId,
              field_id: fieldId,
            });
            successCount++;
          } catch (err: any) {
            console.error(`Failed to add option "${trimmedLabel}":`, err);
          }
        }),
      );

      // Update success message based on number of options added
      if (successCount === lines.length) {
        toaster.create({
          type: 'success',
          title: `Added ${successCount} options successfully`,
        });
      } else if (successCount > 0) {
        toaster.create({
          type: 'warning',
          title: `Added ${successCount} of ${lines.length} options`,
          description: 'Some options could not be added',
        });
      } else {
        throw new Error('Failed to add any options');
      }

      // Clear input and refresh options list
      setOptions('');
      onSuccess?.();

      // Refresh existing options list
      const response = await api.get(`/field-options/${fieldId}`);
      setExistingOptions(response.data.data || []);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to create options',
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <VStack direction={'row'} gap={2} align={'stretch'}>
      <Text fontSize={'sm'}>Field Options</Text>

      {validationErrors.length > 0 && (
        <Alert.Root status='error' mt={2} mb={2} borderRadius='md'>
          <Alert.Indicator />
          <Box>
            <Alert.Description>
              <VStack align='start' gap={1}>
                {validationErrors.map((error, index) => (
                  <Text key={index} fontSize='sm'>
                    {error}
                  </Text>
                ))}
              </VStack>
            </Alert.Description>
          </Box>
        </Alert.Root>
      )}

      <Textarea
        id={'options'}
        name={'options'}
        value={options}
        placeholder={'First Option,option1\nSecond Option,option2'}
        rows={8}
        onChange={(e) => {
          setOptions(e.target.value);
          if (validationErrors.length) setValidationErrors([]);
        }}
        disabled={isLoading || isSubmitting}
      />

      <Text fontSize='xs' color='fg.muted'>
        Enter each option on a new line with format: Label,value
      </Text>

      <Box mt={4}>
        <Button
          colorPalette='brand'
          type={'submit'}
          onClick={onSubmit}
          loading={isSubmitting || isLoading}
          loadingText={isLoading ? 'Loading options...' : 'Adding...'}
          disabled={options.trim() === '' || isLoading}
        >
          Add Options
        </Button>
      </Box>
    </VStack>
  );
}
